// Web Push (navigateur) : enregistre le service worker, s'abonne avec la clé VAPID du
// backend, envoie l'abonnement (/push/register), et remonte les accusés (delivered/read)
// depuis les messages du service worker. Réception multi-onglets même app fermée.
import { getEntraConfig } from '../auth/entraConfig';
// Corrélation de session : ces appels doivent porter les mêmes en-têtes que le reste
// de l'app, sinon ils atterrissent dans une session synthétisée par le serveur (CCT-259).
import { fetchWithTimeout } from '../api/client';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function postReceipt(base: string, token: string, messageId: number, status: 'delivered' | 'read') {
  try {
    await fetchWithTimeout(`${base}/push/receipt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ message_id: messageId, status }),
    });
  } catch { /* best-effort */ }
}

let wired = false;

// ABONNEMENT DÉJÀ DÉCLARÉ (CCT-259) — `${identité}|${endpoint}`.
//
// `registerWebPush` est appelé depuis `applySession`, donc à CHAQUE application de session :
// connexion, restauration, et surtout renouvellement de jeton. Or l'abonnement push, lui, ne
// change pas : c'est celui du navigateur. Relevé sur une seule session : 59 appels à
// `/push/register` et autant à `/push/vapid-key`, pour ré-écrire 59 fois la même ligne.
//
// La mémoire porte l'IDENTITÉ en plus de l'endpoint : sur un poste partagé, un autre compte
// doit bien ré-enregistrer l'abonnement à son nom, sinon il recevrait les notifications du
// précédent. Elle vit dans le module, donc le temps de la page — un rechargement redéclare,
// ce qui est le bon compromis : l'appel devient rare sans jamais devenir impossible.
let alreadyRegistered: string | null = null;

/** À appeler à la déconnexion : le prochain compte doit ré-enregistrer l'abonnement. */
export function resetWebPushRegistration(): void {
  alreadyRegistered = null;
}

export async function registerWebPush(
  token: string | null,
  options: { userKey?: string | null; force?: boolean } = {},
): Promise<void> {
  if (!token) return;
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') return;
  const base = (getEntraConfig().apiBaseUrl || '').replace(/\/$/, '');
  if (!base) return;
  const identite = options.userKey || 'anonyme';
  // Court-circuit AVANT tout appel réseau, mais APRÈS vérification que l'abonnement du
  // navigateur est toujours le même : il peut être révoqué ou renouvelé sans nous prévenir.
  // `getSubscription()` est local — il ne coûte pas d'aller-retour.
  if (!options.force && alreadyRegistered) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const actuel = reg ? await reg.pushManager.getSubscription() : null;
      if (actuel && alreadyRegistered === `${identite}|${actuel.endpoint}`) return;
    } catch {
      /* on ne sait pas : on refait le tour complet, c'est le cas sûr */
    }
  }
  try {
    // Permission EN PREMIER (avant tout autre await) : appelée depuis un clic
    // (bouton « Activer »), l'activation utilisateur est ainsi préservée et le
    // navigateur affiche bien le prompt. Sur restauration de session (sans geste),
    // requestPermission() se résout sans prompt si l'état n'est pas déjà 'granted'.
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return;
    const reg = await navigator.serviceWorker.register('/sw.js');
    // `fetchWithTimeout` et non `fetch` (CCT-259) : c'est lui qui pose les en-têtes de
    // corrélation (session, rang, application). En `fetch` nu, ces appels tombaient dans une
    // session SYNTHÉTISÉE par le serveur — un fourre-tout `srv-…`, sans application ni action
    // cliente, où l'on ne pouvait plus les rattacher à quoi que ce soit.
    const keyRes = await fetchWithTimeout(`${base}/push/vapid-key`);
    const { publicKey } = await keyRes.json();
    if (!publicKey) return;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
      });
    }
    const json: any = sub.toJSON();
    await fetchWithTimeout(`${base}/push/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ kind: 'web', endpoint: json.endpoint, keys: json.keys, platform: 'web' }),
    });
    // Mémorisé APRÈS confirmation du serveur : un enregistrement qui a échoué doit être
    // retenté au prochain passage, pas considéré comme acquis.
    alreadyRegistered = `${identite}|${sub.endpoint}`;
    // Accusés : le service worker prévient le client → on poste delivered/read (authentifié).
    if (!wired) {
      wired = true;
      navigator.serviceWorker.addEventListener('message', (ev: MessageEvent) => {
        const d: any = ev.data || {};
        if (d.type === 'cc-push' && d.messageId) postReceipt(base, token, d.messageId, 'delivered');
        if (d.type === 'cc-push-open' && d.messageId) postReceipt(base, token, d.messageId, 'read');
      });
    }
  } catch { /* silencieux : pas de push si refusé/indisponible */ }
}

/**
 * Pourquoi le push web est (ou non) possible ici — CCT-115.
 *
 * Cas mobile PIÉGEUX : sur iPhone/iPad, Safari en ONGLET ne permet PAS le push. Apple ne
 * l'autorise que pour un site AJOUTÉ À L'ÉCRAN D'ACCUEIL (affiché en `standalone`). Sans ce
 * diagnostic, l'utilisateur active les notifications, ne reçoit rien, et rien ne l'explique :
 * `registerWebPush` se contentait d'un `return` silencieux.
 */
export type PushSupport = 'ok' | 'ios-needs-install' | 'unsupported' | 'denied';

export function webPushSupport(): PushSupport {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'unsupported';
  const ua = navigator.userAgent || '';
  // iPadOS récent se présente comme un Mac : on le reconnaît au tactile.
  const isIOS = /iPad|iPhone|iPod/.test(ua)
    || (/Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1);
  const standalone = (window.matchMedia?.('(display-mode: standalone)')?.matches)
    || (navigator as any).standalone === true;
  if (isIOS && !standalone) return 'ios-needs-install';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') {
    return 'unsupported';
  }
  if (Notification.permission === 'denied') return 'denied';
  return 'ok';
}
